import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Award, X, Lightbulb, MessageSquare, History, Send, Loader2 } from 'lucide-react';

// ------- Score level mapping (must mirror ScoreBadge) ----------
const levelFor = (score) => {
  const v = Number(score || 0);
  if (v >= 75) return { label: 'ممتاز', color: '#10b981' };
  if (v >= 50) return { label: 'جيد', color: '#d4af37' };
  if (v >= 30) return { label: 'متوسط', color: '#f59e0b' };
  return { label: 'يحتاج تحسين', color: '#ef4444' };
};

// Rule-based, level-driven improvement suggestions tied to the metrics in WeeklyReports.
const suggestionFor = (barberRow) => {
  const score = Number(barberRow?.loyalty_score || 0);
  const linked = Number(barberRow?.linked_pct || 0);
  const newPct = Number(barberRow?.new_pct || 0);
  const ret = Number(barberRow?.returning_pct || 0);
  const unlinked = Number(barberRow?.unlinked_pct || 0);

  const tips = [];

  if (score < 30) {
    tips.push('تقييمك الحالي يحتاج تحسيناً جوهرياً — التركيز هذا الأسبوع على نقطتين فقط أفضل من محاولة تحسين كل شيء.');
  } else if (score < 50) {
    tips.push('تقييمك "متوسط" — أنت قريب جداً من الانتقال لـ "جيد"؛ تحسين بسيط بنسبة الربط أو الجدد يكفي.');
  } else if (score < 75) {
    tips.push('تقييمك "جيد" — للوصول إلى "ممتاز" ركّز على رفع الجدد ودعوة عملائك الحاليين لإحضار صديق.');
  } else {
    tips.push('تقييمك ممتاز! حافظ على هذا المستوى وكن قدوة لزملائك. اطلب من الإدارة فرصة لتدريب حلاق جديد.');
  }

  if (linked < 50) {
    tips.push(`نسبة ربط بطاقة الولاء عندك ${linked.toFixed(0)}% — اجعلها عادة تلقائية: عند كل زيارة اسأل العميل عن رقمه قبل بدء الخدمة.`);
  } else if (linked < 75) {
    tips.push(`نسبة الربط جيدة (${linked.toFixed(0)}%) — استهدف 80%+ بطلب الرقم بمجرد جلوس العميل، قبل الكلام عن الخدمة.`);
  } else {
    tips.push(`نسبة ربط ممتازة (${linked.toFixed(0)}%) — حافظ عليها واطلب من الإدارة عرضاً خاصاً للمسجلين.`);
  }

  if (unlinked > 30) {
    tips.push(`${unlinked.toFixed(0)}% من زياراتك بدون بطاقة ولاء — كل زيارة غير مرتبطة تعني فقدان فرصة لإرجاع العميل عبر النقاط.`);
  }

  if (newPct < 15) {
    tips.push(`نسبة العملاء الجدد ${newPct.toFixed(0)}% منخفضة — اطلب من عملائك المنتظمين دعوة صديق ووعدهم بمكافأة بسيطة عند نجاح الإحالة.`);
  } else if (newPct >= 25) {
    tips.push(`الجدد نسبتهم ${newPct.toFixed(0)}% — ممتاز! ركّز الآن على تحويلهم لعملاء عائدين بقصة شعر مميّزة وتذكيرهم بموعد قادم.`);
  }

  if (ret < 30) {
    tips.push(`نسبة العائدين عندك ${ret.toFixed(0)}% — اقترح للعميل ميعاد زيارته القادم وأرسل له تذكير قبل يومين.`);
  } else if (ret >= 50) {
    tips.push(`نسبة العائدين عالية (${ret.toFixed(0)}%) — هؤلاء كنزك، خصّص لهم خدمة سريعة بدون انتظار.`);
  }

  return tips;
};

const BarberRatingDialog = ({ open, onClose, barberRow, weekStart, weekLabel, canEdit, currentUserRole }) => {
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [saving, setSaving] = useState(false);

  const score = Number(barberRow?.loyalty_score || 0);
  const level = levelFor(score);
  const tips = suggestionFor(barberRow);
  const aiSuggestionText = tips.join('\n• ');

  useEffect(() => {
    if (!open || !barberRow?.barber_id) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [trendRes, notesRes] = await Promise.all([
          api.get(`/weekly-reports/barber-trend/${barberRow.barber_id}?weeks=8`),
          api.get(`/weekly-reports/barber-feedback/${barberRow.barber_id}?limit=10`),
        ]);
        if (!active) return;
        setHistory(trendRes.data?.series || []);
        setNotes(notesRes.data || []);
      } catch (e) {
        if (!active) return;
        toast.error(e?.response?.data?.detail || 'فشل تحميل البيانات');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [open, barberRow?.barber_id]);

  const submitNote = async () => {
    if (!noteInput.trim()) {
      toast.error('اكتب مقترحك قبل الإرسال');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/weekly-reports/barber-feedback/${barberRow.barber_id}`, {
        week_start: weekStart,
        week_label: weekLabel,
        score,
        level: level.label,
        ai_suggestion: '• ' + aiSuggestionText,
        barber_note: noteInput.trim(),
      });
      toast.success('تم حفظ المقترح');
      setNotes((prev) => [res.data, ...prev]);
      setNoteInput('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !barberRow) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="barber-rating-modal"
    >
      <div
        className="bg-card border border-white/10 rounded-xl p-5 max-w-3xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-bold"
              style={{ background: `${level.color}22`, color: level.color, border: `1px solid ${level.color}55` }}
            >
              <Award className="w-4 h-4" /> {score.toFixed(0)} · {level.label}
            </div>
            <div>
              <h3 className="font-bold text-lg">{barberRow.barber_name}</h3>
              <p className="text-xs text-muted-foreground">{weekLabel || 'الأسبوع الحالي'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-white text-xl" data-testid="close-rating-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* AI Suggestions */}
        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-lg p-4 mb-4" data-testid="rating-ai-suggestion">
          <div className="flex items-center gap-2 mb-2 text-emerald-400 font-bold text-sm">
            <Lightbulb className="w-4 h-4" />
            مقترحات تحسين مخصصة لك
          </div>
          <ul className="space-y-2 text-sm">
            {tips.map((t, i) => (
              <li key={i} className="bg-emerald-500/10 border-r-2 border-emerald-500 rounded-md p-2 text-emerald-100/90">
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* History Table */}
        <div className="bg-secondary/30 border border-white/10 rounded-lg p-3 mb-4" data-testid="rating-history-table">
          <div className="flex items-center gap-2 mb-2 text-blue-400 font-bold text-sm">
            <History className="w-4 h-4" />
            تسلسل تقييمك (آخر 8 أسابيع)
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : history.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2 text-center">لا توجد بيانات سابقة بعد</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="p-2 text-start">الأسبوع</th>
                    <th className="p-2 text-center">التقييم</th>
                    <th className="p-2 text-center">المستوى</th>
                    <th className="p-2 text-center">ربط %</th>
                    <th className="p-2 text-center">جدد %</th>
                    <th className="p-2 text-center">عائد %</th>
                    <th className="p-2 text-center">زيارات</th>
                    <th className="p-2 text-center">التغيّر</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, idx) => {
                    const prev = history[idx - 1];
                    const delta = prev ? h.loyalty_score - prev.loyalty_score : null;
                    const lvl = levelFor(h.loyalty_score);
                    return (
                      <tr key={h.week_start} className="border-t border-white/5">
                        <td className="p-2">{h.week_label}</td>
                        <td className="p-2 text-center font-bold" style={{ color: lvl.color }}>{Number(h.loyalty_score || 0).toFixed(0)}</td>
                        <td className="p-2 text-center text-xs" style={{ color: lvl.color }}>{lvl.label}</td>
                        <td className="p-2 text-center">{Number(h.linked_pct || 0).toFixed(0)}</td>
                        <td className="p-2 text-center">{Number(h.new_pct || 0).toFixed(0)}</td>
                        <td className="p-2 text-center">{Number(h.returning_pct || 0).toFixed(0)}</td>
                        <td className="p-2 text-center">{h.visits_count}</td>
                        <td className="p-2 text-center">
                          {delta === null ? '—' : (
                            <span className={delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-muted-foreground'}>
                              {delta > 0 ? '+' : ''}{delta.toFixed(0)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Previous notes */}
        {notes.length > 0 && (
          <div className="bg-secondary/20 border border-white/10 rounded-lg p-3 mb-4" data-testid="rating-previous-notes">
            <div className="flex items-center gap-2 mb-2 text-amber-400 font-bold text-sm">
              <MessageSquare className="w-4 h-4" />
              ملاحظاتك/مقترحاتك السابقة
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {notes.map((n) => (
                <div key={n.id} className="bg-amber-500/5 border border-amber-500/20 rounded-md p-2 text-xs">
                  <div className="flex items-center justify-between text-muted-foreground mb-1">
                    <span>{n.week_label || '—'} · {n.level} · {Number(n.score).toFixed(0)}</span>
                    <span>{new Date(n.created_at).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }).split('-').reverse().join('/')}</span>
                  </div>
                  <p className="text-sm">{n.barber_note}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Barber input */}
        {canEdit && (
          <div className="bg-primary/5 border border-primary/30 rounded-lg p-3" data-testid="rating-note-form">
            <div className="flex items-center gap-2 mb-2 text-primary font-bold text-sm">
              <MessageSquare className="w-4 h-4" />
              اكتب مقترحك للتحسين (سيقرأه المدير)
            </div>
            <textarea
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="مثلاً: سأطلب من كل عميل تسجيل بطاقة الولاء قبل بدء الخدمة، وأرسل تذكير للعملاء الذين لم يزوروا منذ 20 يوم..."
              className="w-full bg-background border border-white/10 rounded-md p-3 text-sm resize-y"
              data-testid="rating-note-input"
            />
            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
              <span>{noteInput.length} / 2000</span>
              <Button
                onClick={submitNote}
                disabled={saving || !noteInput.trim()}
                className="bg-primary text-primary-foreground gap-2"
                size="sm"
                data-testid="rating-note-submit"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                حفظ المقترح
              </Button>
            </div>
            {currentUserRole === 'admin' && (
              <p className="text-[10px] text-muted-foreground mt-2">ملاحظة: المدير يمكنه الحفظ نيابة عن الحلاق.</p>
            )}
          </div>
        )}
        {!canEdit && (
          <div className="bg-secondary/20 border border-white/10 rounded-lg p-3 text-xs text-muted-foreground text-center" data-testid="rating-readonly-notice">
            🔒 يمكنك فقط مشاهدة تقييم زملائك. اضغط على تقييمك أنت لإضافة مقترح.
          </div>
        )}
      </div>
    </div>
  );
};

export default BarberRatingDialog;
